import { useState, useRef, useEffect } from 'react';
import { X, Sparkle, EnvelopeSimple, WarningCircle, CheckCircle } from '@phosphor-icons/react';

/**
 * Email-gate modal — opens before Stripe checkout to verify the customer
 * typed their email correctly. Two-field confirmation (idea from Cynthia
 * Tippmann, 2026-06-20) catches ~80-90% of typos before they cause a lost
 * license-key delivery.
 *
 * Flow:
 *   1. Customer clicks a tier CTA on MigrationCrunch
 *   2. This modal opens. Two email fields must match AND look valid.
 *   3. We also surface a friendly "did you mean gmail.com?" hint for the
 *      most common domain typos (gmial, yahooo, hotmial, etc.).
 *   4. On Confirm, we call the parent's onConfirm(email) which forwards
 *      the email to the backend as `customer_email` for Stripe.
 *      Stripe pre-fills the email AND locks the field (read-only at checkout),
 *      so the customer cannot change it.
 *
 * Props:
 *   - open (bool)
 *   - tierLabel (string) — e.g. "Starter Pro · $599"
 *   - onCancel ()
 *   - onConfirm (email) — caller is responsible for closing the modal
 */

// Common domain typos → suggested fix. Lowercase keys.
const TYPO_FIXES = {
  'gmial.com': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gmail.cm': 'gmail.com',
  'gmial.con': 'gmail.com',
  'gmail.con': 'gmail.com',
  'yhoo.com': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yahoo.co': 'yahoo.com',
  'yaoo.com': 'yahoo.com',
  'hotmial.com': 'hotmail.com',
  'hotmai.com': 'hotmail.com',
  'hotmial.co': 'hotmail.com',
  'hotmal.com': 'hotmail.com',
  'hormail.com': 'hotmail.com',
  'outlok.com': 'outlook.com',
  'outlokk.com': 'outlook.com',
  'outloo.com': 'outlook.com',
  'outloook.com': 'outlook.com',
  'aol.co': 'aol.com',
  'aoll.com': 'aol.com',
  'icloud.co': 'icloud.com',
  'icoud.com': 'icloud.com',
  'iclooud.com': 'icloud.com',
  'comcst.net': 'comcast.net',
  'comast.net': 'comcast.net',
};

function suggestFix(email) {
  if (!email || !email.includes('@')) return null;
  const [, domain] = email.split('@');
  if (!domain) return null;
  const fix = TYPO_FIXES[domain.toLowerCase()];
  if (!fix) return null;
  return email.replace(domain, fix);
}

// Looks-like-an-email check (deliberately permissive — Stripe will do the strict check).
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default function EmailGateModal({ open, tierLabel, onCancel, onConfirm }) {
  const [email1, setEmail1] = useState('');
  const [email2, setEmail2] = useState('');
  const [touched2, setTouched2] = useState(false);
  const firstInputRef = useRef(null);

  // Reset on open/close so the modal is always fresh.
  useEffect(() => {
    if (open) {
      setEmail1('');
      setEmail2('');
      setTouched2(false);
      setTimeout(() => firstInputRef.current?.focus(), 50);
    }
  }, [open]);

  if (!open) return null;

  const trimmed1 = email1.trim();
  const trimmed2 = email2.trim();
  const looksValid = EMAIL_REGEX.test(trimmed1);
  const matches = trimmed1 && trimmed1.toLowerCase() === trimmed2.toLowerCase();
  const suggestion = suggestFix(trimmed1);
  const canSubmit = looksValid && matches && !suggestion;

  const acceptSuggestion = () => {
    if (!suggestion) return;
    setEmail1(suggestion);
    // Don't auto-fill #2 — they should still re-type to confirm.
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    onConfirm(trimmed1.toLowerCase());
  };

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onCancel}
      data-testid="mc-email-gate-backdrop"
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-md w-full overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="mc-email-gate-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-br from-[#0F0F1A] to-[#1a1f3a] text-white px-6 py-5 relative">
          <button
            onClick={onCancel}
            className="absolute top-3 right-3 text-white/50 hover:text-white p-1 rounded"
            aria-label="Close"
            data-testid="mc-email-gate-close"
          >
            <X size={18} weight="bold" />
          </button>
          <div className="flex items-center gap-2 text-[10px] font-bold tracking-widest text-[#F39C12] uppercase mb-2">
            <Sparkle size={11} weight="fill" /> {tierLabel || 'Almost there'}
          </div>
          <h2 className="text-xl font-black leading-snug" data-testid="mc-email-gate-headline">
            Where should we send your license key?
          </h2>
          <p className="text-xs text-white/65 mt-2">
            Type your email twice. We&apos;ll lock it on the Stripe page so a typo
            can&apos;t happen.
          </p>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Email
            </label>
            <div className="relative">
              <EnvelopeSimple
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                ref={firstInputRef}
                type="email"
                value={email1}
                onChange={(e) => setEmail1(e.target.value)}
                onPaste={(e) => {
                  // Block clipboard re-paste into the confirmation field so
                  // the customer actually has to retype — the whole point.
                  setTimeout(() => setEmail1(e.target.value || email1), 0);
                }}
                placeholder="you@yourcompany.com"
                className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-md text-sm text-slate-900 caret-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#F39C12] focus:border-transparent"
                data-testid="mc-email-gate-input-1"
                autoComplete="email"
                required
              />
            </div>
          </div>

          {/* Typo suggestion */}
          {suggestion && (
            <div
              className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2.5 text-xs text-slate-800 flex items-start gap-2"
              data-testid="mc-email-gate-suggestion"
            >
              <WarningCircle size={14} weight="fill" className="text-amber-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                Did you mean{' '}
                <button
                  type="button"
                  onClick={acceptSuggestion}
                  className="font-bold text-[#0B5394] underline hover:text-[#073763]"
                  data-testid="mc-email-gate-accept-suggestion"
                >
                  {suggestion}
                </button>
                ?
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">
              Confirm email
            </label>
            <div className="relative">
              <EnvelopeSimple
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="email"
                value={email2}
                onChange={(e) => setEmail2(e.target.value)}
                onBlur={() => setTouched2(true)}
                onPaste={(e) => {
                  // Block paste on the confirmation field — the customer MUST
                  // retype. That's what catches the typo.
                  e.preventDefault();
                }}
                placeholder="Retype your email"
                className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-md text-sm text-slate-900 caret-slate-900 bg-white focus:outline-none focus:ring-2 focus:ring-[#F39C12] focus:border-transparent"
                data-testid="mc-email-gate-input-2"
                autoComplete="off"
                required
              />
            </div>
            {touched2 && trimmed2 && !matches && (
              <p
                className="text-xs text-rose-600 mt-1.5 flex items-center gap-1"
                data-testid="mc-email-gate-mismatch"
              >
                <WarningCircle size={12} weight="fill" /> Emails don&apos;t match. Check both carefully.
              </p>
            )}
            {matches && looksValid && !suggestion && (
              <p
                className="text-xs text-emerald-600 mt-1.5 flex items-center gap-1"
                data-testid="mc-email-gate-match-ok"
              >
                <CheckCircle size={12} weight="fill" /> Match. Ready to continue.
              </p>
            )}
          </div>

          {/* Reassurance */}
          <p className="text-[11px] text-slate-500 leading-relaxed">
            Your license email is the only way to come back to your migration
            later from a different device. Spending an extra 5 seconds here is
            worth it.
          </p>

          {/* Buttons */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2.5 border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-bold rounded-md"
              data-testid="mc-email-gate-cancel"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="flex-1 px-4 py-2.5 bg-[#F39C12] hover:bg-[#E08C00] disabled:bg-slate-200 disabled:text-slate-400 disabled:cursor-not-allowed text-black text-sm font-black rounded-md transition-colors"
              data-testid="mc-email-gate-continue"
            >
              Continue to Stripe →
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
